import Component from '@glimmer/component'
import { service } from '@ember/service'
import { get } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'

type LessonGuideRow = {
  grades: string
  lessons: string
  fictions: string[]
  nonfictions: string[]
}

interface Signature {
  Element: HTMLTableElement
}

export interface LessonGuideDataItem {
  gradePositions: { from: number; to: number }
  maps: { from: number; to: number }
  lessons: { from: number; to: number }
  fictions: string[]
  nonFictions: string[]
}

export const LessionGuideData: LessonGuideDataItem[] = [
  {
    gradePositions: { from: 2, to: 3 },
    maps: { from: 1, to: 6 },
    lessons: { from: 1, to: 30 },
    fictions: ['Map 1 Fantasy', 'Map 3 First Person Narrative', 'Map 5 Realistic Fiction'],
    nonFictions: ['Map 2 Animals Report', 'Map 4 Procedures', 'Map 6 All About Dinosaurs'],
  },
  {
    gradePositions: { from: 4, to: 5 },
    maps: { from: 7, to: 12 },
    lessons: { from: 31, to: 60 },
    fictions: ['Map 7 Fairytales', 'Map 9 Story Settings', 'Map 11 Character Description'],
    nonFictions: ['Map 8 Animal Explanations', "Map 10 What's Your Opinion?", 'Map 12 Person Recount (Science)'],
  },
  {
    gradePositions: { from: 6, to: 7 },
    maps: { from: 13, to: 17 },
    lessons: { from: 61, to: 85 },
    fictions: ['Map 13 Tell Me a Story', 'Map 15 Make Me Laugh', 'Map 17 Science Fiction'],
    nonFictions: ['Map 14 Advertisements', 'Map 16 Animal Adaptions', 'Persuasive Long Writes'],
  },
]

export class LessonGuideTableWriting extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  get rows(): LessonGuideRow[] {
    return LessionGuideData.map((row) => {
      const grades = `${this.gradeSets.nameFromGradeSetByPosition(
        row.gradePositions.from,
      )}–${this.gradeSets.nameFromGradeSetByPosition(row.gradePositions.to)}`
      const { lessons } = row
      return {
        grades,
        lessons: `Lessons ${lessons.from}–${lessons.to}`,
        fictions: row.fictions,
        nonfictions: row.nonFictions,
      }
    })
  }

  <template>
    <table class="table-fixed" ...attributes>
      <thead>
        <tr>
          <th class="w-1/6"></th>
          <th class="w-1/5"></th>
          <th class="table-box table-box-header bg-juicy-orange-200 text-center" colspan="2">
            {{t "tables.lessonGuide.headers.writingLegends.title"}}
          </th>
        </tr>
        <tr class="h-10">
          <th class="table-box table-box-header bg-juicy-orange-100 text-center">
            {{t "tables.lessonGuide.headers.writingLegends.yearGrade"}}
          </th>
          <th class="table-box table-box-header bg-juicy-orange-100 text-center">
            {{t "tables.lessonGuide.headers.writingLegends.lessons"}}
          </th>
          <th class="table-box table-box-header bg-juicy-orange-100 text-center">
            {{t "tables.lessonGuide.headers.writingLegends.fiction"}}
          </th>
          <th class="table-box table-box-header bg-juicy-orange-100 text-center">
            {{t "tables.lessonGuide.headers.writingLegends.nonFiction"}}
          </th>
        </tr>
      </thead>
      <tbody>
        {{#each this.rows as |row|}}
          {{#each row.fictions as |fiction index|}}
            <tr>
              {{#if (eq index 0)}}
                <td class="table-box table-box-cell bg-juicy-orange-50 w-[10%] p-2" rowspan="3">{{row.grades}}</td>
                <td class="table-box table-box-cell bg-juicy-orange-50 p-2" rowspan="3">{{row.lessons}}</td>
              {{/if}}
              <td class="bg-juicy-orange-50 border border-white p-2">{{fiction}}</td>
              <td class="table-box table-box-cell bg-juicy-orange-50 p-2">{{get row.nonfictions index}}</td>
            </tr>
          {{/each}}
        {{/each}}
      </tbody>
    </table>
  </template>
}

export default LessonGuideTableWriting
